// Angular imports
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { JwtHelperService } from '@auth0/angular-jwt';
import { plainToInstance } from 'class-transformer';

// RxJs imports
import { Observable, Subject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Service imports
import { AuthService } from '../auth.service';
import { DatadogService } from '../datadog.service';
import { EnvironmentConfigService } from '../environment-config.service';
import { RPCacheRegistrationService } from '../shared-services/rp-cache-registration-service';

// Models imports
import { ForgotUserNameParameterDto } from '../../_models/forgot-user-name-parameter-dto';
import { ResultStatusDto } from '../../_models/result-status-dto';
import { UserProfileImageParameterDto } from '../../_models/user-profile-image-parameter-dto';
import { UserPropertyResultDto } from '../../_models/user-property-result-dto';
import { AccountParameterDto } from '../../_models/account-parameter-dto';
import { UserProfileImageResultDto } from '../../_models/user-profile-image-result-dto';
import { ImpersonateParameterDto } from '../../_models/impersonate-parameter-dto';
import { ImpersonateResultDto } from '../../_models/impersonate-result-dto';
import { UserInfoResultDto } from '../../_models/user-info-result-dto';
import { ResetPasswordParametersDto } from '../../_models/reset-password-parameters-dto';
import { ValidateUserInfoParameterDto } from '../../_models/validate-user-info-dto';
import { CreateUserParameterDto } from '../../_models/create-user-parameter-dto';
import { BaseResidentParameterDto } from '../../_models/base-resident-parameter-dto';
import { ActionItemConfigResultDto } from '../../_models/action-item-config-result-dto';
import { ValidateUserInfoResultDto } from '../../_models/validate-userinfo-result-dto';
import { BoardAnnouncementResultDto } from '../../_models/board-announcement-result-dto';
import { BoardAccessResultDto } from '../../_models/board-access-result-dto';
import { AnnouncementsParametersDto } from '../../_models/announcements-parameters-dto';
import { AnnouncementsResultDto } from '../../_models/announcements-result-dto';
import { BaseParameterDto } from '../../_models/base-parameter-dto';
import { UserDashboardResultDto } from '../../_models/user-dashboard-result-dto';
import { QuickPaymentPropertiesResultDto } from '../../_models/quick-payment-properties-result-dto';
import { QuickPaymentPropertiesParameterDto } from '../../_models/quick-payment-properties-parameter-dto';
import { BrandingConfigurationResultDto } from '../../_models/branding-configuration-result-dto';
import { GenerateValidateEmailParameterDto } from '../../_models/generate-validate-email-parameter-dto';
import { RegistrationEditorParametersDto } from '../../_models/registration/registration-editor-parameters-dto';
import { RegisterWithoutRecaptchaParametersDto } from '../../_models/registration/register-without-recaptcha-parameters-dto';
import { RegistrationEditorResultDto } from '../../_models/registration/registration-editor-result-dto';
import { ForgotPasswordTokenParametersDto } from '../../_models/forgot-password-token-parameters-dto';
import { ValidateRegistrationCredentialsDto } from '../../_models/validate-registration-credentials-dto';
import { ValidateRegistrationCredentialsResultDto } from '../../_models/validate-registration-credentials-result-dto';

// Constant imports
import { Constants } from '../../_constants/constants';
import { CacheDuration } from '../../_constants/cache-duration';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';
import { isDevOrLocal } from '../../_constants/feature-flags';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  jwtHelper = new JwtHelperService();
  decodedToken: any;
  heading: string;

  userInfo: UserInfoResultDto;

  private _impersonatedAccountNo: string;
  private _userDashboardResult: UserDashboardResultDto;
  private _selectedProperty: UserPropertyResultDto;
  private _hasProperties = true;
  private _boardAccess: BoardAccessResultDto[];
  private _subjectBoardAccessChanged: Subject<BoardAccessResultDto[]> =
    new Subject();
  private _subjectPropertySelectionChanged: Subject<UserPropertyResultDto> =
    new Subject();
  private _subjectNoProperties: Subject<boolean> = new Subject();
  private _subjectNoActionItem: Subject<boolean> = new Subject();
  private _subjectImpersonateChanged = new Subject();
  private _subjectImpersonateHistoryChanged: Subject<
    ImpersonateParameterDto[]
  > = new Subject();
  private _subjectNewPropertyAdded: Subject<UserPropertyResultDto> =
    new Subject();
  private _subjectSelectDefaultProperty: Subject<UserPropertyResultDto[]> =
    new Subject();
  private _subjectShowAddPropertyModal: Subject<void> = new Subject();

  private _brandingInforamtion: BrandingConfigurationResultDto;
  private _subjectBrandingInforamtionChanged: Subject<BrandingConfigurationResultDto> =
    new Subject();
  // private _subjectAnnouncementsChanged: Subject<boolean> = new Subject();

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private datadogService: DatadogService,
    private cacheRegistrationService: RPCacheRegistrationService,
    private envConfig: EnvironmentConfigService
  ) {
    RxjsUtils.subscribeToSubject(
      this.authService.getSubjectLogout(),
      (flag: boolean) => {
        if (flag === true) {
          this.userInfo = undefined;
          this._userDashboardResult = undefined;
          this._selectedProperty = undefined;
          this._hasProperties = true;
          this._boardAccess = undefined;
        }
      }
    );

    // Impersonation swaps the effective identity, so any cached board grants
    // belong to the previous user and must be dropped — the next board lookup
    // refetches for the impersonated user.
    RxjsUtils.subscribeToSubject(this._subjectImpersonateChanged, () => {
      this._boardAccess = undefined;
    });
  }

  forgotPassword(
    forgotPasswordParameterDto: ForgotPasswordTokenParametersDto
  ): Observable<any> {
    return this.httpClient.post(
      this.envConfig.ccResidentPortalApiUrl + 'user/forgotpassword',
      forgotPasswordParameterDto
    );
  }

  resetPassword(
    resetPasswordParametersDto: ResetPasswordParametersDto
  ): Observable<ResultStatusDto> {
    return this.httpClient
      .put<ResultStatusDto>(
        this.envConfig.ccResidentPortalApiUrl + 'user/resetpassword',
        resetPasswordParametersDto
      )
      .pipe(
        map((responseData) => plainToInstance(ResultStatusDto, responseData))
      );
  }

  createUser(userInfo: CreateUserParameterDto): Observable<ResultStatusDto> {
    return this.httpClient
      .post<ResultStatusDto>(
        this.envConfig.ccResidentPortalApiUrl + 'user/create',
        userInfo
      )
      .pipe(
        map((responseData) => plainToInstance(ResultStatusDto, responseData))
      );
  }

  validateUser(
    userInfo: ValidateUserInfoParameterDto
  ): Observable<ValidateUserInfoResultDto> {
    return this.httpClient
      .put(this.envConfig.ccResidentPortalApiUrl + 'user/validate', userInfo)
      .pipe(
        map((responseData) =>
          plainToInstance(ValidateUserInfoResultDto, responseData)
        )
      );
  }

  // to retrieve user_name from email
  sendForgotUsernameEmail(
    dto: ForgotUserNameParameterDto
  ): Observable<ResultStatusDto> {
    return this.httpClient
      .post(this.envConfig.ccResidentPortalApiUrl + 'user/forgotusername', dto)
      .pipe(
        map((responseData) => plainToInstance(ResultStatusDto, responseData))
      );
  }

  updateProfile(
    data: UserProfileImageParameterDto
  ): Observable<ResultStatusDto> {
    return this.httpClient
      .put<ResultStatusDto>(
        this.envConfig.ccResidentPortalApiUrl + 'user/update/profileimage',
        data
      )
      .pipe(
        map((responseData) => plainToInstance(ResultStatusDto, responseData))
      );
  }

  getProfileImage(
    clearCache: boolean = false
  ): Observable<UserProfileImageResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      `user/profileimage?clearCache=${clearCache}`;
    this.cacheRegistrationService.registerForCache(url, clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get<UserProfileImageResultDto>(url, options)
      .pipe(
        map((responseData) =>
          plainToInstance(UserProfileImageResultDto, responseData)
        )
      );
  }

  getUserProperties(
    clearCache: boolean = false
  ): Observable<UserPropertyResultDto[]> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      `user/properties?clearCache=${clearCache}`;
    this.cacheRegistrationService.registerForCache(url, clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get<any[]>(url, options)
      .pipe(
        map((responseData: any[]) =>
          plainToInstance(UserPropertyResultDto, responseData)
        )
      );
  }

  getBoardAccess(
    clearCache: boolean = false
  ): Observable<BoardAccessResultDto[]> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      `user/board-access?clearCache=${clearCache}`;
    this.cacheRegistrationService.registerForCache(url, clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient.get<any[]>(url, options).pipe(
      map((responseData: any[]) => {
        this._boardAccess = plainToInstance(
          BoardAccessResultDto,
          responseData || []
        );
        this._subjectBoardAccessChanged.next(this._boardAccess);
        return this._boardAccess;
      }),
      // A board member whose term expired (RES-553) or who simply has no board
      // role gets no list; degrade gracefully to "no board access" so resident
      // content still renders instead of erroring the route.
      catchError(() => {
        this._boardAccess = [];
        this._subjectBoardAccessChanged.next(this._boardAccess);
        return of(this._boardAccess);
      })
    );
  }

  getBoardAccessForSelectedProperty(): BoardAccessResultDto {
    const property = this.getSelectedProperty();
    if (!this._boardAccess || !property) {
      return undefined;
    }
    return this._boardAccess.find(
      (access: BoardAccessResultDto) =>
        access.communityId === property.communityId && access.isTermActive
    );
  }

  hasBoardAccessForSelectedProperty(): boolean {
    return !!this.getBoardAccessForSelectedProperty();
  }

  // Distinguishes "not fetched yet" (undefined) from "fetched, no board grants"
  // (empty array) so the guard only triggers a lookup when nothing is cached.
  isBoardAccessLoaded(): boolean {
    return this._boardAccess !== undefined;
  }

  getSubjectBoardAccessChanged(): Subject<BoardAccessResultDto[]> {
    return this._subjectBoardAccessChanged;
  }

  addUserProperty(data: AccountParameterDto): Observable<ResultStatusDto> {
    return this.httpClient
      .post<ResultStatusDto>(
        this.envConfig.ccResidentPortalApiUrl + 'user/addproperty',
        data
      )
      .pipe(
        map((responseData: any) =>
          plainToInstance(ResultStatusDto, responseData)
        )
      );
  }

  getUserActionItems(
    data: BaseResidentParameterDto
  ): Observable<ActionItemConfigResultDto[]> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'user/actions' +
      data.getQueryString();
    this.cacheRegistrationService.registerForCache(url, data.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get<ActionItemConfigResultDto[]>(url, options)
      .pipe(
        map((responseData: any[]) =>
          plainToInstance(ActionItemConfigResultDto, responseData)
        )
      );
  }

  getSubjectNoProperties(): Subject<boolean> {
    return this._subjectNoProperties;
  }

  updateSubjectNoProperties(value: boolean): void {
    this._hasProperties = value;
    this._subjectNoProperties.next(value);
  }

  hasProperties(): boolean {
    return this._hasProperties;
  }

  getSubjectNoActionItems(): Subject<boolean> {
    return this._subjectNoActionItem;
  }

  updateSubjectNoActionItem(value: boolean): void {
    this._subjectNoActionItem.next(value);
  }

  getSubjectPropertySelectionChanged(): Subject<UserPropertyResultDto> {
    return this._subjectPropertySelectionChanged;
  }

  updateSubjectPropertySelectionChanged(value: UserPropertyResultDto): void {
    this._selectedProperty = value;
    localStorage.setItem(
      StorageKeyConstants.selectedPropertyKey,
      JSON.stringify(value)
    );
    this.cacheRegistrationService.deleteAllCache();
    this._subjectPropertySelectionChanged.next(value);
  }

  getSubjectNewPropertyAdded(): Subject<UserPropertyResultDto> {
    return this._subjectNewPropertyAdded;
  }

  updateSubjectNewPropertyAdded(): void {
    this._subjectNewPropertyAdded.next(this._selectedProperty);
  }

  getSubjectSelectDefaultProperty(): Subject<UserPropertyResultDto[]> {
    return this._subjectSelectDefaultProperty;
  }

  updateSubjectSelectDefaultProperty(
    properties: UserPropertyResultDto[]
  ): void {
    this._subjectSelectDefaultProperty.next(properties);
  }

  getSubjectShowAddPropertyModal(): Subject<void> {
    return this._subjectShowAddPropertyModal;
  }

  showAddPropertyModal(): void {
    this._subjectShowAddPropertyModal.next();
  }

  getSubjectImpersonateHistory(): Subject<ImpersonateParameterDto[]> {
    return this._subjectImpersonateHistoryChanged;
  }

  updateSubjectImpersonateHistory(value: ImpersonateParameterDto[]) {
    this._subjectImpersonateHistoryChanged.next(value);
  }

  getSubjectImpersonateChanged() {
    return this._subjectImpersonateChanged;
  }

  updateSubjectImpersonateChanged(value: boolean): void {
    this.cacheRegistrationService.deleteAllCache();
    this._subjectImpersonateChanged.next(value);
  }

  getCurrentUserId(): string {
    return this.getUserIDByTokenName(StorageKeyConstants.apiTokenName);
  }

  getUserIDByTokenName(tokenName: string) {
    const token = localStorage.getItem(tokenName);
    if (!!token) {
      const decoded = this.jwtHelper.decodeToken(token);
      if (!!decoded.unique_name) {
        return decoded.unique_name;
      } else if (!!decoded.nameid) {
        return decoded.nameid;
      }
    }
    return null;
  }

  getCurrentUserInfo(): UserInfoResultDto {
    if (this.userInfo == null && this.authService.isLoggedIn == true) {
      const userInfoJson = localStorage.getItem(StorageKeyConstants.userInfo);
      if (!!userInfoJson) {
        this.userInfo = JSON.parse(userInfoJson);
      }
    }
    return plainToInstance(UserInfoResultDto, this.userInfo);
  }

  getSelectedProperty(): UserPropertyResultDto {
    const property = localStorage.getItem(
      StorageKeyConstants.selectedPropertyKey
    );
    if (!!property && !!property.length) {
      this._selectedProperty = plainToInstance(
        UserPropertyResultDto,
        JSON.parse(property)
      );
    }
    return this._selectedProperty;
  }

  setSelectedProperty(property: UserPropertyResultDto): void {
    this._selectedProperty = property;
    localStorage.setItem(
      StorageKeyConstants.selectedPropertyKey,
      JSON.stringify(property)
    );
    // DEV-ONLY DIAGNOSTIC (RES-554): logs board-access on property change.
    // Gated by the runtime hostname check (isDevMode() is build-time, so it is
    // false on the deployed dev host because that runs the prod build artifact).
    // isDevOrLocal() fires on local ng serve AND resident.dev/local.ciranet.com,
    // and stays silent in qa/prod. Reflects false until the login/bootstrap
    // prefetch of getBoardAccess() resolves.
    if (isDevOrLocal()) {
      console.log(
        '[RES-554] property change →',
        property?.communityName,
        '(communityId:',
        property?.communityId,
        ') | hasBoardAccess:',
        this.hasBoardAccessForSelectedProperty(),
        '| grant:',
        this.getBoardAccessForSelectedProperty()
      );
    }
  }

  impersonatingUserId(): string {
    if (this.isImpersonated()) {
      return this.getUserIDByTokenName(StorageKeyConstants.apiLoginTokenName);
    }
    return null;
  }

  impersonatedUserId(): string {
    if (this.isImpersonated()) {
      return this.getUserIDByTokenName(StorageKeyConstants.apiTokenName);
    }
    return null;
  }

  impersonatedAccountNo(): string {
    return this._impersonatedAccountNo;
  }

  isImpersonated(): boolean {
    const loggedInToken = localStorage.getItem(
      StorageKeyConstants.apiLoginTokenName
    );
    const token = localStorage.getItem(StorageKeyConstants.apiTokenName);

    return !!loggedInToken && !!token;
  }

  canImpersonate(): boolean {
    const userInfo = this.getCurrentUserInfo();
    if (userInfo != null && userInfo.canImpersonate == true) {
      return true;
    }

    const originalUserInfo = JSON.parse(
      localStorage.getItem(StorageKeyConstants.loggedInUserInfo)
    );
    if (originalUserInfo != null && originalUserInfo.canImpersonate == true) {
      return true;
    }
    return false;
  }

  endImpersonation() {
    // remove impersonated user token
    localStorage.removeItem(StorageKeyConstants.apiTokenName);
    this.decodedToken = null;
    this._impersonatedAccountNo = null;

    // check if logged-in user token is present
    const loggedInUserToken = localStorage.getItem(
      StorageKeyConstants.apiLoginTokenName
    );
    if (!!loggedInUserToken) {
      localStorage.setItem(StorageKeyConstants.apiTokenName, loggedInUserToken);
      this.decodedToken = this.jwtHelper.decodeToken(loggedInUserToken);
      localStorage.removeItem(StorageKeyConstants.apiLoginTokenName);

      const userInfo = JSON.parse(
        localStorage.getItem(StorageKeyConstants.loggedInUserInfo)
      ); // retrieve backup userinfo
      this.setCurrentUserInfo(userInfo);

      // Restore original user in Datadog
      this.datadogService.setUser(plainToInstance(UserInfoResultDto, userInfo));

      localStorage.removeItem(StorageKeyConstants.loggedInUserInfo);

      // Clear the impersonated user's selected property so the display name
      // reverts to the original logged-in user
      this._selectedProperty = undefined;
      localStorage.removeItem(StorageKeyConstants.selectedPropertyKey);

      this.updateSubjectImpersonateChanged(false);
    }
  }

  impersonate(
    model: ImpersonateParameterDto
  ): Observable<ImpersonateResultDto> {
    const impersonating = this.isImpersonated();
    // if currently impersonating stop impersonation
    if (impersonating === true) {
      this.endImpersonation();
    }

    return this.httpClient
      .post<ImpersonateResultDto>(
        this.envConfig.ccResidentPortalApiUrl + 'user/impersonate',
        model
      )
      .pipe(
        map((responseData) =>
          plainToInstance(ImpersonateResultDto, responseData)
        ),
        map((impersonatedUser: ImpersonateResultDto) => {
          if (impersonatedUser) {
            this._impersonatedAccountNo = model.userName;
            // store logged in user token
            const loggedInUserToken = localStorage.getItem(
              StorageKeyConstants.apiTokenName
            );
            if (!!loggedInUserToken) {
              localStorage.setItem(
                StorageKeyConstants.apiLoginTokenName,
                loggedInUserToken
              );
            }

            // store logged in user info
            const userInfo = this.getCurrentUserInfo();
            if (!!loggedInUserToken) {
              localStorage.setItem(
                StorageKeyConstants.loggedInUserInfo,
                JSON.stringify(userInfo)
              ); // backup current user info
            }

            // store impersonated user token
            localStorage.setItem(
              StorageKeyConstants.apiTokenName,
              impersonatedUser.token
            );
            this.decodedToken = this.jwtHelper.decodeToken(
              impersonatedUser.token
            );

            // store impersonated user info
            this.setCurrentUserInfo(impersonatedUser); // store whole user

            // Track impersonated user in Datadog
            this.datadogService.setUser(
              plainToInstance(UserInfoResultDto, impersonatedUser),
              true, // isImpersonated
              userInfo.userId // originalUserId
            );

            this.updateSubjectImpersonateChanged(true);
          }
          return impersonatedUser;
        })
      );
  }

  setCurrentUserInfo(userInfo: UserInfoResultDto): void {
    this.userInfo = userInfo;
    localStorage.setItem(
      StorageKeyConstants.userInfo,
      JSON.stringify(userInfo)
    );
  }

  setImpersonateHistory(impersonateUserId: ImpersonateParameterDto) {
    let impersonateHistory: ImpersonateParameterDto[] = [];
    const impersonateHistoryData = localStorage.getItem(
      StorageKeyConstants.impersonateHistory
    );

    if (!!impersonateHistoryData) {
      impersonateHistory = JSON.parse(impersonateHistoryData);
    }

    const index = impersonateHistory.findIndex(
      (x) => x.userName === impersonateUserId.userName
    );
    if (index >= 0) {
      impersonateHistory.unshift(impersonateHistory.splice(index, 1)[0]);
    } else {
      impersonateHistory.unshift(impersonateUserId);
    }

    localStorage.setItem(
      StorageKeyConstants.impersonateHistory,
      JSON.stringify(impersonateHistory)
    );
    this.updateSubjectImpersonateHistory(this.getImpersonateHistory());
  }

  getImpersonateHistory(): ImpersonateParameterDto[] {
    const impersontaeHistoryData = localStorage.getItem(
      StorageKeyConstants.impersonateHistory
    );
    let impersonateHistory: ImpersonateParameterDto[] = [];
    if (!!impersontaeHistoryData) {
      impersonateHistory = JSON.parse(impersontaeHistoryData);
    }

    return impersonateHistory;
  }

  isITUser(): boolean {
    return this.isInRole('IT');
  }

  isInternal(): boolean {
    return this.isInRole('INTERNAL');
  }

  isInRole(role: string): boolean {
    const token = localStorage.getItem(StorageKeyConstants.apiTokenName);
    const decoded = this.jwtHelper.decodeToken(token);
    if (!!decoded && !!decoded.role) {
      const userRoles = decoded.role.toUpperCase().split(',');
      if (userRoles.length > 0 && userRoles.indexOf(role) >= 0) {
        return true;
      }
    }
    return false;
  }

  userPortalInfo(clearCache: boolean = false): Observable<UserInfoResultDto> {
    const url =
      this.envConfig.ccAuthenticationApiUrl.replace('/auth/', '/') +
      `user/portalinfo?clearCache=${clearCache}`;
    this.cacheRegistrationService.registerForCache(url, clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const params = new HttpParams().set('PortalType', Constants.portalTypeCode);
    const options = { params, headers };

    return this.httpClient
      .get<UserInfoResultDto>(url, options)
      .pipe(map((response) => plainToInstance(UserInfoResultDto, response)));
  }

  getBoardAnnouncements(
    data: BaseResidentParameterDto
  ): Observable<BoardAnnouncementResultDto[]> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'community/announcements' +
      data.getQueryString();
    this.cacheRegistrationService.registerForCache(url, data.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get<BoardAnnouncementResultDto[]>(url, options)
      .pipe(
        map((responseData: any[]) =>
          plainToInstance(BoardAnnouncementResultDto, responseData)
        )
      );
  }

  getAnnouncementCount(param: AnnouncementsParametersDto): Observable<number> {
    const url =
      this.envConfig.ccAuthenticationApiUrl.replace('/auth/', '/') +
      `user/announcementscount` +
      param.getQueryString();
    this.cacheRegistrationService.registerForCache(url, param.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get<number>(url, options)
      .pipe(map((responseData: any) => responseData));
  }

  getAnnouncements(
    param: AnnouncementsParametersDto
  ): Observable<AnnouncementsResultDto[]> {
    const url =
      this.envConfig.ccAuthenticationApiUrl.replace('/auth/', '/') +
      `user/announcements` +
      param.getQueryString();
    this.cacheRegistrationService.registerForCache(url, param.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get<AnnouncementsResultDto[]>(url, options)
      .pipe(
        map((responseData: AnnouncementsResultDto[]) =>
          plainToInstance(AnnouncementsResultDto, responseData)
        )
      );
  }

  /*
      This method is implemented with a local cache as this is not dependent on
      property selection and due to property selection change the client cache is getting cleared.
      Hence maintaining a local copy as alternate cache
    */
  getUserDashboardItems(
    param: BaseParameterDto
  ): Observable<UserDashboardResultDto> {
    if (param.clearCache === true) {
      this._userDashboardResult = undefined;
    }
    if (!!this._userDashboardResult) {
      return of(this._userDashboardResult);
    } else {
      const url =
        this.envConfig.ccResidentPortalApiUrl +
        'user/dashboard' +
        param.getQueryString();
      this.cacheRegistrationService.registerForCache(url, param.clearCache);
      const headers = this.cacheRegistrationService.getHttpRequestHeaders(
        url,
        CacheDuration.fifteenMinute
      );
      const options = { headers };

      return this.httpClient.get(url, options).pipe(
        map((responseData: any) => {
          this._userDashboardResult = plainToInstance(
            UserDashboardResultDto,
            responseData
          );
          return this._userDashboardResult;
        })
      );
    }
  }

  getQuickPaymentProperties(
    param: QuickPaymentPropertiesParameterDto
  ): Observable<QuickPaymentPropertiesResultDto[]> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'user/quickpaymentproperties' +
      param.getQueryString();
    this.cacheRegistrationService.registerForCache(url, param.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get<QuickPaymentPropertiesResultDto[]>(url, options)
      .pipe(
        map((responseData: QuickPaymentPropertiesResultDto[]) =>
          plainToInstance(QuickPaymentPropertiesResultDto, responseData)
        )
      );
  }

  getBrandingInforamtion(): BrandingConfigurationResultDto {
    return this._brandingInforamtion;
  }

  setBrandingInforamtion(info: BrandingConfigurationResultDto): void {
    this._brandingInforamtion = info;
  }

  getSubjectBrandingInforamtionChanged(): Subject<BrandingConfigurationResultDto> {
    return this._subjectBrandingInforamtionChanged;
  }

  updateSubjectBrandingInforamtionChanged(
    value: BrandingConfigurationResultDto
  ): void {
    this._brandingInforamtion = value;
    this._subjectBrandingInforamtionChanged.next(this._brandingInforamtion);
  }

  generateValidationEmail(
    userInfo: GenerateValidateEmailParameterDto
  ): Observable<ResultStatusDto> {
    return this.httpClient
      .post(
        this.envConfig.ccResidentPortalApiUrl + 'user/validationemail',
        userInfo
      )
      .pipe(
        map((responseData) => plainToInstance(ResultStatusDto, responseData))
      );
  }

  getUserRegistrationEditor(
    baseParams: RegistrationEditorParametersDto
  ): Observable<RegistrationEditorResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'user/registration' +
      baseParams.getQueryString();

    return this.httpClient
      .get<RegistrationEditorResultDto>(url)
      .pipe(
        map((responseData: RegistrationEditorResultDto) =>
          plainToInstance(RegistrationEditorResultDto, responseData)
        )
      );
  }

  validateNewUserAccountWithoutRecaptcha(
    params: RegisterWithoutRecaptchaParametersDto
  ): Observable<RegistrationEditorResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'user/validatenew' +
      params.getQueryString();

    return this.httpClient
      .get<RegistrationEditorResultDto>(url)
      .pipe(
        map((responseData: RegistrationEditorResultDto) =>
          plainToInstance(RegistrationEditorResultDto, responseData)
        )
      );
  }

  validateRegistrationCredentials(
    credentials: ValidateRegistrationCredentialsDto
  ): Observable<ValidateRegistrationCredentialsResultDto> {
    const url =
      this.envConfig.ccAuthenticationApiUrl + 'validateRegistrationCredentials';
    return this.httpClient
      .post<ValidateRegistrationCredentialsResultDto>(url, credentials)
      .pipe(
        map((responseData: ValidateRegistrationCredentialsResultDto) =>
          plainToInstance(
            ValidateRegistrationCredentialsResultDto,
            responseData
          )
        )
      );
  }
}
